import {useMutation, useQuery} from '@apollo/client';
import {Form, Select, Table, Tooltip} from 'antd';
import {GET_FINANCIAL_INSTRUMENT_LANGUAGE_VERSIONS} from 'graphql/queries/assetManagement';
import {useMemo, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {toast} from 'react-toastify';
import moment from 'moment';
import styles from '../assetClassOverview.module.scss';
import review from './translationReview.module.scss';
import {Button} from '@dabafinance/react-components';
import backIcon from 'images/back.png';
import {notificationLanguages} from 'utils/constants';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import {
  APPROVE_INSTRUMENT_TRANSLATION,
  TRANSLATE_FINANCIAL_INSTRUMENT,
} from 'graphql/mutations/assetManagement';
import {instrumentKeysFor} from '../../modules/instrumentKeys';
import {TranslationPreviewModal} from './TranslationPreviewModal';

const LANGUAGE_LABELS = {EN: 'English', FR: 'French'};

const languageLabel = language => LANGUAGE_LABELS[language] ?? language ?? '—';

const formatDate = value =>
  value ? moment(value).format('DD MMM YYYY, hh:mm A') : null;

const Pill = ({tone, children, title}) => {
  const pill = (
    <span className={`${review.pill} ${review[tone]}`}>{children}</span>
  );
  return title ? <Tooltip title={title}>{pill}</Tooltip> : pill;
};

const CLOSED_PREVIEW = {open: false, language: null};

export const AssetInstrumentLanguages = () => {
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [openConfirmTranslationModal, setOpenConfirmTranslationModal] =
    useState(false);
  const [pendingApproval, setPendingApproval] = useState(null);
  const [preview, setPreview] = useState(CLOSED_PREVIEW);
  const {id, key} = useParams();
  const navigate = useNavigate();
  const {state} = useLocation();

  const backTo =
    state?.backTo ?? `/dashboard/asset-management/class/${key}/instruments`;

  // One mutation for every instrument type since the migration — the old
  // per-type translateBond / translateMutualFund no longer exist.
  const [resolver, {loading: loadingTranslation}] = useMutation(
    TRANSLATE_FINANCIAL_INSTRUMENT,
  );
  const [approve, {loading: approving}] = useMutation(
    APPROVE_INSTRUMENT_TRANSLATION,
  );

  const {data, refetch, loading} = useQuery(
    GET_FINANCIAL_INSTRUMENT_LANGUAGE_VERSIONS,
    {
      variables: {
        getFinancialInstrumentLanguageVersionsId: id,
        assetClassKey: key,
      },
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'no-cache',
      onError: error => toast.error(error.message),
    },
  );

  const versions = data?.getFinancialInstrumentLanguageVersions?.data;
  const sourceLanguage = versions?.language;
  // Navigation state disappears when returning from an editor or opening a
  // shared URL. Resolve the concrete type from the record: EQUITIES can be an
  // IPO or a public equity, and each editor queries a different GraphQL type.
  const instrumentKey = data?.instrument?.data?.key;
  const updateRoute = instrumentKeysFor(key).find(
    instrument => instrument.key === instrumentKey,
  )?.route;

  /**
   * One row per language, carrying its review state.
   *
   * `translations` is the real source: it is what says whether a language is an unpublished
   * machine draft or approved copy. `languageVersions` is kept as the fallback for older
   * instruments that predate the review fields — those rows default to APPROVED server-side,
   * which is the same treatment mobile gives them.
   */
  const rows = useMemo(() => {
    if (!versions) return [];
    const translations = versions.translations ?? [];
    if (translations.length > 0) {
      return translations.map(row => ({...row, key: row.language}));
    }
    return (versions.languageVersions ?? []).map(language => ({
      // Funds can come back with a null entry — their `language` is null in the
      // data. EN is the gateway's own fallback.
      language: language ?? 'EN',
      key: language ?? 'EN',
      reviewStatus: 'APPROVED',
      translationSource: 'HUMAN',
      stale: false,
      safeToRetranslate: false,
    }));
  }, [versions]);

  const runApproval = language => {
    approve({variables: {id, language}})
      .then(({data: result}) => {
        const response = result?.approveInstrumentTranslation;
        if (response?.__typename === 'Error' || response?.statusCode === 400) {
          toast.error(response?.message);
          return;
        }
        toast.success(response?.message);
        setPendingApproval(null);
        setPreview(CLOSED_PREVIEW);
        refetch();
      })
      .catch(error => toast.error(error.message));
  };

  const columns = [
    {
      title: 'Language',
      dataIndex: 'language',
      key: 'language',
      render: (text, record) => (
        <div className={review.languageCell}>
          <strong>{languageLabel(record.language)}</strong>
          {record.language === sourceLanguage && (
            <span className={review.sourceNote}>Source copy</span>
          )}
        </div>
      ),
    },
    {
      title: 'Written by',
      dataIndex: 'translationSource',
      key: 'translationSource',
      render: (text, record) =>
        record.translationSource === 'MACHINE' ? (
          <Pill
            tone="toneInfo"
            title="Produced by DeepL, not written by a person.">
            Machine
          </Pill>
        ) : (
          <Pill tone="toneNeutral" title="Written or edited by an admin.">
            Human
          </Pill>
        ),
    },
    {
      title: 'Status',
      dataIndex: 'reviewStatus',
      key: 'reviewStatus',
      render: (text, record) => (
        <div className={review.pillRow}>
          {record.reviewStatus === 'PENDING_REVIEW' ? (
            <Pill
              tone="toneWarn"
              title="Saved, but NOT shown to users in this language until it is approved.">
              Pending review
            </Pill>
          ) : (
            <Pill
              tone="toneOk"
              title="Live — users reading this language see this copy.">
              Approved
            </Pill>
          )}
          {record.stale && (
            <Pill
              tone="toneWarn"
              title="The source copy changed after this translation was made, so it now states the old wording.">
              Stale
            </Pill>
          )}
        </div>
      ),
    },
    {
      title: 'Last reviewed',
      dataIndex: 'reviewedAt',
      key: 'reviewedAt',
      render: (text, record) => {
        const at = formatDate(record.reviewedAt);
        if (!at) return <span className={review.sourceNote}>—</span>;
        return (
          <div className={review.reviewedCell}>
            <span>{at}</span>
            {record.reviewedBy && <span>{record.reviewedBy}</span>}
          </div>
        );
      },
    },
    {
      title: 'Actions',
      dataIndex: 'operation',
      key: 'actions',
      render: (text, record) => {
        const isSource = record.language === sourceLanguage;
        return (
          <div className={review.rowActions}>
            <Button
              onClick={() =>
                setPreview({open: true, language: record.language})
              }
              className={styles.button}
              type="secondary"
              label="Preview"
            />
            {/* Approving source copy is meaningless — it is the original, not a
                translation of anything — and the gateway rejects it outright. */}
            {!isSource && record.reviewStatus === 'PENDING_REVIEW' && (
              <Button
                onClick={() => setPendingApproval(record.language)}
                className={styles.button}
                label="Approve"
              />
            )}
            <Button
              disabled={!updateRoute}
              onClick={() =>
                updateRoute &&
                navigate(
                  `/dashboard/asset-management/update/${updateRoute}/${key}/${id}/${record.language}`,
                  {state: {backTo}},
                )
              }
              className={styles.button}
              type="secondary"
              label="Edit"
            />
          </div>
        );
      },
    },
  ];

  const selectedRow = rows.filter(row => row.language === selectedLanguage)[0];
  const previewRow = rows.filter(row => row.language === preview.language)[0];

  return (
    <div className={styles.languageVersions}>
      <div className={styles.header}>
        <img onClick={() => navigate(backTo)} src={backIcon} alt="" />
        <h1>
          {/* Read the query this page actually runs — it asks for
              getFinancialInstrumentLanguageVersions, so the asset-class key
              was always undefined and the title always rendered empty. */}
          {versions?.name} Language Versions
        </h1>
      </div>

      <div className={review.translatePanel}>
        <h3>Translate into another language</h3>
        <p className={review.panelNote}>
          Translation is billed per character and is saved as soon as it
          finishes, but it is not shown to users until it is approved below.
          {sourceLanguage
            ? ` This instrument is written in ${languageLabel(sourceLanguage)}.`
            : ''}
        </p>
        <Form
          onFinish={() => setOpenConfirmTranslationModal(true)}
          layout="vertical">
          <Form.Item
            label="Select Language to Translate"
            name="language"
            className={styles.language}
            rules={[
              {
                required: true,
                message: 'Please select a language to translate.',
              },
            ]}>
            <Select
              placeholder="French"
              onChange={setSelectedLanguage}
              style={{width: 320}}
              name="type">
              {notificationLanguages.map(name => (
                <Select.Option key={name.key} value={name.key}>
                  {name.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <SubmitButton label="Proceed" />
        </Form>
      </div>

      <div>
        <Table columns={columns} dataSource={rows} loading={loading} />
      </div>

      <ConfirmModal
        openModal={openConfirmTranslationModal}
        setOpenModal={() => setOpenConfirmTranslationModal(false)}
        title="Confirm Translation"
        confirmText={
          // Re-translating over copy a person wrote or signed off on destroys that work;
          // only an unreviewed machine draft is safe to overwrite silently.
          selectedRow && !selectedRow.safeToRetranslate
            ? `A ${languageLabel(
                selectedLanguage,
              )} version already exists and has been approved or edited by a person. Translating again replaces it with fresh machine output and it will need reviewing before users see it. Continue?`
            : 'Are you sure you want to translate this?'
        }
        loading={loadingTranslation}
        handleOperation={() => {
          resolver({
            variables: {id, language: selectedLanguage},
          })
            .then(({data}) => {
              const result = data?.translateFinancialInstrument;
              if (
                result?.__typename === 'Error' ||
                result?.statusCode === 400
              ) {
                toast.error(result?.message);
              } else {
                setOpenConfirmTranslationModal(false);
                refetch();
                toast.success(result?.message);
              }
            })
            // A rejected mutation used to surface as an uncaught runtime
            // overlay rather than a toast.
            .catch(error => toast.error(error.message));
        }}
      />

      <ConfirmModal
        openModal={!!pendingApproval}
        setOpenModal={() => setPendingApproval(null)}
        title="Publish this translation"
        confirmText={`Approving publishes the ${languageLabel(
          pendingApproval,
        )} copy to everyone reading that language. Preview it first if you have not already.`}
        loading={approving}
        handleOperation={() => runApproval(pendingApproval)}
      />

      <TranslationPreviewModal
        instrumentId={id}
        language={preview.language}
        openModal={preview.open}
        setOpenModal={() => setPreview(CLOSED_PREVIEW)}
        approving={approving}
        canApprove={
          !!previewRow &&
          previewRow.reviewStatus === 'PENDING_REVIEW' &&
          previewRow.language !== sourceLanguage
        }
        onApprove={() => runApproval(preview.language)}
      />
    </div>
  );
};
